import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";

const SOCIAL_LINKS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/melbiwellness",
    Icon: Instagram,
    testId: "footer-social-instagram",
    // Instagram's official brand gradient (purple → pink → orange)
    style: {
      backgroundImage:
        "linear-gradient(45deg, #F58529 0%, #DD2A7B 45%, #8134AF 70%, #515BD4 100%)",
    },
    className: "text-white border-transparent hover:opacity-90 hover:brightness-105",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/melbiwellness",
    Icon: Facebook,
    testId: "footer-social-facebook",
    // Facebook's official brand blue
    style: { backgroundColor: "#1877F2" },
    className: "text-white border-transparent hover:opacity-90 hover:brightness-105",
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#F3F1EC] border-t border-[#E8E5DF] mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          <div>
            <Logo />
            <p className="mt-4 font-display text-base font-semibold text-[#2C2B29]">
              Melbi Wellness
            </p>
            <p className="mt-1 text-sm text-[#5E5C58] leading-relaxed max-w-xs">
              Clinician guided wellness care.
            </p>

            {/* LegitScript certification seal — DO NOT resize (LegitScript
                requires the seal be shown at the provided 73x79 dimensions). */}
            <div className="mt-6 flex items-center gap-4">
            <div data-testid="footer-legitscript-seal">
              <a
                href="https://www.legitscript.com/websites/?checker_keywords=melbiwellness.com"
                target="_blank"
                rel="noopener noreferrer"
                title="Verify LegitScript Approval for www.melbiwellness.com"
              >
                <img
                  src="https://static.legitscript.com/seals/51328587.png"
                  alt="Verify Approval for www.melbiwellness.com"
                  width="73"
                  height="79"
                />
              </a>
            </div>
            <a
              href="https://www.trustpilot.com/review/melbiwellness.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-trustpilot-link"
              className="text-sm font-semibold text-[#2C2B29] underline decoration-[#E8E5DF] underline-offset-4 hover:text-[#C96A4E] hover:decoration-[#C96A4E] transition-colors"
            >
              Read our reviews on Trustpilot
            </a>
            </div>
          </div>

          <div>
            <h4 className="font-display text-base font-semibold text-[#2C2B29] mb-4">
              Learn
            </h4>
            <ul className="space-y-3 text-sm text-[#5E5C58]">
              <li><Link to="/#how-it-works" className="hover:text-[#C96A4E] transition-colors">How It Works</Link></li>
              <li><Link to="/what-to-expect" className="hover:text-[#C96A4E] transition-colors">What to Expect</Link></li>
              <li><Link to="/#wellness-categories" className="hover:text-[#C96A4E] transition-colors">The Wellness Edit</Link></li>
              <li><Link to="/trust-your-source" className="hover:text-[#C96A4E] transition-colors">Trust Your Source</Link></li>
              <li><Link to="/faq" className="hover:text-[#C96A4E] transition-colors">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-base font-semibold text-[#2C2B29] mb-4">
              Legal
            </h4>
            <ul className="space-y-3 text-sm text-[#5E5C58]">
              <li><Link to="/medical-disclosures" data-testid="footer-medical-disclosures-link" className="font-semibold text-[#2C2B29] hover:text-[#C96A4E] transition-colors">Medical Disclosures</Link></li>
              <li><Link to="/legal/terms" className="hover:text-[#C96A4E] transition-colors">Terms and Conditions</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-[#C96A4E] transition-colors">Privacy Policy</Link></li>
              <li><Link to="/legal/refund" className="hover:text-[#C96A4E] transition-colors">Refund Policy</Link></li>
              <li><Link to="/legal/hipaa" className="hover:text-[#C96A4E] transition-colors">HIPAA Privacy Policy</Link></li>
              <li><Link to="/service-areas" className="hover:text-[#C96A4E] transition-colors">Service Areas</Link></li>
              <li><Link to="/contact" className="hover:text-[#C96A4E] transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-base font-semibold text-[#2C2B29] mb-4">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-[#5E5C58]">
              <li>
                <a href="mailto:hello@melbiwellness.com" className="hover:text-[#C96A4E] transition-colors">
                  hello@melbiwellness.com
                </a>
              </li>
            </ul>

            <div
              data-testid="footer-social-links"
              className="mt-5 flex items-center gap-3"
            >
              {SOCIAL_LINKS.map(({ name, href, Icon, testId, style, className }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Melbi Wellness on ${name}`}
                  data-testid={testId}
                  style={style}
                  className={`inline-flex items-center justify-center h-9 w-9 rounded-full border transition-all shadow-sm ${className}`}
                >
                  <Icon size={16} strokeWidth={2} />
                </a>
              ))}
            </div>

            <p
              data-testid="footer-emergency-notice"
              className="mt-5 text-xs text-[#5E5C58] leading-relaxed"
            >
              <strong className="font-bold uppercase tracking-[0.12em] text-[#C96A4E]">Not for emergencies.</strong>{" "}
              If you are experiencing a medical emergency, call{" "}
              <strong className="text-[#2C2B29]">911</strong> or go to your nearest emergency department.
            </p>
          </div>
        </div>

        <div
          data-testid="footer-disclosures"
          className="mt-12 pt-8 border-t border-[#E8E5DF] text-[11px] md:text-xs text-[#5E5C58] leading-relaxed"
        >
          <p data-testid="footer-brief-notice" className="max-w-4xl">
            Prescription treatments require licensed clinician review and are prescribed only when
            clinically appropriate. Not all patients qualify. Compounded medications are not
            FDA-approved. Individual results vary.
          </p>
          <p className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
            <Link
              to="/medical-disclosures"
              data-testid="footer-disclosures-cta"
              className="inline-flex items-center gap-1 font-semibold text-[#2C2B29] underline decoration-[#C96A4E] decoration-2 underline-offset-4 hover:text-[#C96A4E] transition-colors"
            >
              Read full Medical Disclosures <ArrowRight size={12} />
            </Link>
            <Link
              to="/legal/refund"
              data-testid="footer-subscription-policy-link"
              className="font-semibold text-[#2C2B29] underline decoration-[#E8E5DF] underline-offset-4 hover:text-[#C96A4E] transition-colors"
            >
              Cancellation, Refund, and Subscription Policy
            </Link>
          </p>

          <p data-testid="footer-jurisdictional-notice" className="mt-6">
            <strong className="text-[#2C2B29]">Jurisdictional Notice.</strong>{" "}
            Services are available only in jurisdictions where affiliated medical
            providers are licensed to practice and where pharmacy partners are
            authorized to dispense medications. Availability may vary by state
            and is subject to applicable laws, regulations, and licensure
            requirements. Melbi Wellness serves patients in all 50 U.S. states
            and the District of Columbia through affiliated licensed medical
            providers and authorized pharmacy partners. The jurisdictions we
            serve are in accordance with LegitScript Healthcare Certification
            Standards 5 and 8.
          </p>

          {/* Partner Pharmacy Information — full width, end of compliance block */}
          <div
            data-testid="partner-pharmacy-info"
            className="mt-6 pt-6 border-t border-[#E8E5DF]"
          >
            <h5 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-[#2C2B29]">
              Partner Pharmacy Information
            </h5>
            <p className="mt-3">
              <strong className="text-[#2C2B29]">VialsRX:</strong>{" "}
              6220 Westpark Dr, Houston, TX 77057 &middot;{" "}
              Phone:{" "}
              <a
                href="tel:+17134975590"
                className="text-[#2C2B29] hover:text-[#C96A4E] transition-colors"
              >
                (713) 497-5590
              </a>{" "}
              &middot;{" "}
              <a
                href="https://vials.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2C2B29] hover:text-[#C96A4E] underline decoration-[#E8E5DF] underline-offset-2 transition-colors"
              >
                vials.ai
              </a>
            </p>
            <p className="mt-3">
              Services are available in all 50 US states through affiliated
              medical providers and pharmacy partners operating in accordance
              with applicable state licensure requirements.
            </p>
            <p className="mt-3">
              Please note that certain medications, formulations, or fulfillment
              options may vary depending on state-specific pharmacy regulations
              and dispensing restrictions.
            </p>
          </div>

          <p className="mt-8 text-[11px] text-[#8A9A92]">
            © {new Date().getFullYear()} Melbi Wellness, LLC. All rights reserved.
            No insurance billing.
          </p>
        </div>
      </div>
    </footer>
  );
}
